// =============================================================================
// services/notes.ts — IG-Notes-style STATUS NOTES client.
//
// Thin, crash-safe wrapper over the /notes API (all calls go through api(), so
// they resolve to { success, data?, error? } and never throw). Mirrors the shape
// StoryRow/StatusBubbles consume:
//   - getNotes()  → { myNote, notesByUser } ready to hand straight to StoryRow.
//   - setNote(t)  → create/replace my note (server sets +24h expiry).
//   - clearNote() → delete my note.
//
// The server returns `notes` as a flat list keyed by author; we fold it into a
// Record<userId, StatusNote> so home.tsx can index by story-group userId.
// =============================================================================
import { api } from './api';

// A single status note as returned by the API.
export interface Note {
  id: string;
  userId: string;
  text: string;
  createdAt?: string;
  expiresAt?: string;
  // Author info (present on friends' notes; absent on `myNote`).
  username?: string;
  displayName?: string;
  avatar?: string;
}

// The shape home.tsx wires into StoryRow's note props.
export interface NotesResult {
  /** The current user's own active note, or null if none. */
  myNote: Note | null;
  /** Friends'/followed users' notes, keyed by their userId. */
  notesByUser: Record<string, Note>;
}

// GET /notes — my note + notes from people I follow / am friends with. Always
// resolves (empty result on failure) so the caller can render unconditionally.
export async function getNotes(): Promise<NotesResult> {
  const res = await api<{ myNote?: Note | null; notes?: Note[] }>('/notes');
  if (!res.success || !res.data) {
    return { myNote: null, notesByUser: {} };
  }
  const myNote = res.data.myNote ?? null;
  const notesByUser: Record<string, Note> = {};
  for (const n of Array.isArray(res.data.notes) ? res.data.notes : []) {
    if (n && n.userId && typeof n.text === 'string' && n.text.trim().length > 0) {
      notesByUser[n.userId] = n;
    }
  }
  return { myNote, notesByUser };
}

// POST /notes — create or replace my note. Server enforces one active note per
// user and sets expiresAt = now + 24h. Returns the created note, or null on error.
export async function setNote(text: string): Promise<Note | null> {
  const trimmed = (text || '').trim();
  if (!trimmed) return null;
  const res = await api<Note>('/notes', {
    method: 'POST',
    body: JSON.stringify({ text: trimmed }),
  });
  return res.success && res.data ? res.data : null;
}

// DELETE /notes — clear my note. Returns true on success (idempotent server-side).
export async function clearNote(): Promise<boolean> {
  const res = await api('/notes', { method: 'DELETE' });
  return res.success;
}
